import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Paperclip, Send, X } from "lucide-react";

import type { MeetingChatChannel } from "../../application/useMeetingChat";
import { MAX_CHAT_MESSAGE_LENGTH, chatAuthorRole } from "../../domain/meetingChat";
import {
    ALLOWED_MEETING_FILE_EXTENSIONS,
    MAX_MEETING_FILE_BYTES,
} from "../../domain/meetingFile";
import MeetingChatFile from "./MeetingChatFile";

/**
 * How close to the limit the counter starts saying so. Below this it would be
 * a number nobody needs while they type a sentence.
 */
const COUNTER_FROM = MAX_CHAT_MESSAGE_LENGTH - 200;

/** What the file picker offers. The hook checks again; this only filters. */
const FILE_ACCEPT = ALLOWED_MEETING_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(",");

const MAX_FILE_MEGABYTES = MAX_MEETING_FILE_BYTES / (1024 * 1024);

/**
 * The written channel, beside the faces rather than instead of them.
 *
 * This is the one deliberate difference from the whiteboard. A board needs
 * the whole width to be drawn on, so it takes the tiles' place; a line of
 * text does not, and taking someone's face away to hand them a sentence would
 * cost more than the sentence is worth. On a narrow screen there is no room
 * for both, so there the panel does take over — and the video is one tap away
 * again.
 *
 * Files share the list with messages, in the order they were sent, and are
 * picked from the paperclip beside the draft.
 *
 * The list is a `log` rather than an `alert` region: a screen reader should
 * announce a message that arrives, not interrupt the person listening to the
 * call for it.
 */
export default function MeetingChat({ channel }: { channel: MeetingChatChannel }) {
    const { t, i18n } = useTranslation();
    const { entries, open, setOpen, sending, failed, send, sendFile, fileRejection } = channel;

    const [draft, setDraft] = useState("");
    const listRef = useRef<HTMLOListElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    /**
     * Follow the conversation down.
     *
     * The list scrolls itself rather than an anchor element scrolling into
     * view: `scrollIntoView` moves the nearest scrollable ancestor too, which
     * on a narrow screen is the page holding the video. Only while the panel
     * is up, because a hidden list has no scroll position worth setting.
     */
    useEffect(() => {
        if (!open) return;
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [entries.length, open]);

    const time = (timestamp: number) =>
        new Intl.DateTimeFormat(i18n.language, {
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(timestamp));

    const submit = () => {
        const body = draft;
        setDraft("");
        send(body);
    };

    return (
        <aside
            aria-label={t("meetingChat.title")}
            data-testid="meeting-chat"
            className="flex min-h-0 w-full flex-col gap-2 rounded-xl border border-room-line bg-room-raised p-3 sm:w-80 sm:shrink-0"
        >
            <div className="flex shrink-0 items-center justify-between gap-2">
                <h2 className="text-sm font-semibold tracking-tight text-room-ink">
                    {t("meetingChat.title")}
                </h2>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label={t("meetingChat.close")}
                    className="flex size-11 items-center justify-center rounded-lg text-room-ink-muted transition-colors duration-[--duration-fast] hover:bg-room-hover hover:text-room-ink"
                >
                    <X size={16} aria-hidden="true" />
                </button>
            </div>

            <ol
                ref={listRef}
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1"
            >
                {entries.length === 0 && (
                    <li className="my-auto px-2 text-center text-xs leading-relaxed text-room-ink-muted">
                        {t("meetingChat.empty")}
                    </li>
                )}

                {entries.map((entry) => (
                    <li
                        key={`${entry.kind}-${entry.id}`}
                        className={`flex flex-col gap-0.5 ${
                            entry.mine ? "items-end" : "items-start"
                        }`}
                    >
                        <span className="px-1 text-xs font-medium text-room-ink-muted">
                            {/* The name if they gave one, otherwise which side
                                of the call they are on — never the raw uuid
                                the room knows them by. */}
                            {entry.mine
                                ? t("meetingChat.you")
                                : entry.authorName ||
                                  t(`meetingChat.role.${chatAuthorRole(entry.authorId)}`)}
                            {" · "}
                            {time(entry.timestamp)}
                        </span>
                        {entry.kind === "file" ? (
                            <MeetingChatFile file={entry} />
                        ) : (
                            <p
                                className={`max-w-[85%] whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-sm leading-relaxed ${
                                    entry.mine
                                        ? "bg-room-accent text-white"
                                        : "border border-room-line bg-room-base text-room-ink"
                                }`}
                            >
                                {entry.body}
                            </p>
                        )}
                    </li>
                ))}
            </ol>

            {fileRejection && (
                <p role="alert" className="shrink-0 text-xs font-medium text-room-live">
                    {t(`meetingChat.file.rejected.${fileRejection}`, {
                        size: MAX_FILE_MEGABYTES,
                    })}
                </p>
            )}

            {failed && (
                <p role="alert" className="shrink-0 text-xs font-medium text-room-live">
                    {t("meetingChat.sendFailed")}
                </p>
            )}

            <form
                className="flex shrink-0 items-end gap-2"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_ACCEPT}
                    tabIndex={-1}
                    aria-hidden="true"
                    className="hidden"
                    data-testid="meeting-chat-file-input"
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        // Cleared so picking the same file again still fires.
                        event.target.value = "";
                        if (file) sendFile(file);
                    }}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label={t("meetingChat.file.attach")}
                    title={t("meetingChat.file.attach")}
                    className="room-control size-11 shrink-0 px-0"
                >
                    <Paperclip size={16} aria-hidden="true" />
                </button>
                <label htmlFor="meeting-chat-draft" className="sr-only">
                    {t("meetingChat.draftLabel")}
                </label>
                <textarea
                    id="meeting-chat-draft"
                    value={draft}
                    rows={1}
                    maxLength={MAX_CHAT_MESSAGE_LENGTH}
                    placeholder={t("meetingChat.placeholder")}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        // Enter sends, because this is a conversation and not
                        // a document. Shift+Enter is the way to a new line.
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            submit();
                        }
                    }}
                    className="room-field max-h-28 flex-1 resize-y py-2.5"
                />
                <button
                    type="submit"
                    disabled={sending || draft.trim().length === 0}
                    aria-label={t("meetingChat.send")}
                    className="room-control room-control-primary size-11 shrink-0 px-0 disabled:opacity-50"
                >
                    <Send size={16} aria-hidden="true" />
                </button>
            </form>

            {draft.length >= COUNTER_FROM && (
                <p
                    role="status"
                    className="shrink-0 text-right text-xs text-room-ink-muted"
                >
                    {t("meetingChat.remaining", {
                        count: MAX_CHAT_MESSAGE_LENGTH - draft.length,
                    })}
                </p>
            )}
        </aside>
    );
}
